// src/components/complaints/ComplaintManagement.js
import React, { useState, useEffect } from 'react';
import { supabase, supabaseAdmin } from '../../config/supabase.config';
import SubmitComplaint from './SubmitComplaint';
import ComplaintList from './ComplaintList';
import ComplaintDetails from './ComplaintDetails';
import '../../pages/UserDashboard.css';
import './ComplaintManagement.css';

function ComplaintManagement({ userData, onBack }) {
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('list');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  
  const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';

  const categories = [
    { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
    { value: 'cleanliness', label: 'Cleanliness', icon: '🧹' },
    { value: 'security', label: 'Security', icon: '🔒' },
    { value: 'noise', label: 'Noise', icon: '🔇' },
    { value: 'parking', label: 'Parking', icon: '🚗' },
    { value: 'amenities', label: 'Amenities', icon: '🏊' },
    { value: 'other', label: 'Other', icon: '📌' }
  ];

  const statuses = [
    { value: 'pending', label: 'Pending', color: '#ffc107' },
    { value: 'acknowledged', label: 'Acknowledged', color: '#17a2b8' },
    { value: 'in_progress', label: 'In Progress', color: '#fd7e14' },
    { value: 'resolved', label: 'Resolved', color: '#28a745' },
    { value: 'closed', label: 'Closed', color: '#6c757d' }
  ];

  useEffect(() => {
    fetchComplaints();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, [darkMode]);

  useEffect(() => {
    filterComplaints();
  }, [complaints, filterStatus, filterCategory, searchTerm]);

  const fetchComplaints = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('complaints')
        .select(`
          *,
          creator:residents!complaints_created_by_fkey(name, flat_number),
          assignee:residents!complaints_assigned_to_fkey(name, flat_number),
          resolver:residents!complaints_resolved_by_fkey(name, flat_number)
        `)
        .order('created_at', { ascending: false });

      if (!isAdmin) {
        query = query.eq('created_by', userData.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setComplaints(data || []);
    } catch (error) {
      console.error('Error fetching complaints:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterComplaints = () => {
    let filtered = [...complaints];

    if (filterStatus !== 'all') {
      filtered = filtered.filter(c => c.status === filterStatus);
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(c => c.category === filterCategory);
    }

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.complaint_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredComplaints(filtered);
  };

  const handleComplaintSubmit = () => {
    fetchComplaints();
    setActiveView('list');
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const getStats = () => {
    const stats = {
      total: complaints.length,
      pending: complaints.filter(c => c.status === 'pending').length,
      inProgress: complaints.filter(c => c.status === 'in_progress').length,
      resolved: complaints.filter(c => c.status === 'resolved').length
    };
    return stats;
  };

  const stats = getStats();

  if (activeView === 'submit') {
    return (
      <SubmitComplaint
        userData={userData}
        onBack={() => setActiveView('list')}
        onSubmit={handleComplaintSubmit}
        darkMode={darkMode}
      />
    );
  }

  if (activeView === 'details' && selectedComplaint) {
    return (
      <ComplaintDetails
        complaint={selectedComplaint}
        userData={userData}
        isAdmin={isAdmin}
        onBack={() => {
          setActiveView('list');
          fetchComplaints();
        }}
        darkMode={darkMode}
      />
    );
  }

  return (
    <div className={`dashboard-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={onBack}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <button
            className={`menu-item ${activeView === 'list' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('list');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">📋</span>
            <span className="menu-label">All Complaints</span>
          </button>
          
          <button
            className={`menu-item ${activeView === 'submit' ? 'active' : ''}`}
            onClick={() => {
              setActiveView('submit');
              closeMobileMenu();
            }}
          >
            <span className="menu-icon">➕</span>
            <span className="menu-label">New Complaint</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          <div className="filter-section">
            <label className="filter-label">Filter by Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="sidebar-select"
            >
              <option value="all">All Status</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          
          <div className="filter-section">
            <label className="filter-label">Filter by Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="sidebar-select"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">📝 Complaints</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="complaint-header">
            <h2 className="page-title">📝 Complaint Management</h2>
            <button
              onClick={() => setActiveView('submit')}
              className="btn-primary"
            >
              + Submit Complaint
            </button>
          </div>

          {/* Stats Cards */}
          <div className="dashboard-grid stats-grid">
            <div className="dashboard-card stat-card gradient-purple">
              <div className="stat-content">
                <h3>📊 Total</h3>
                <p className="stat-value">{stats.total}</p>
                <small>All Complaints</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-yellow">
              <div className="stat-content">
                <h3>⏳ Pending</h3>
                <p className="stat-value">{stats.pending}</p>
                <small>Awaiting Action</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-blue">
              <div className="stat-content">
                <h3>🔄 In Progress</h3>
                <p className="stat-value">{stats.inProgress}</p>
                <small>Being Resolved</small>
              </div>
            </div>

            <div className="dashboard-card stat-card gradient-green">
              <div className="stat-content">
                <h3>✅ Resolved</h3>
                <p className="stat-value">{stats.resolved}</p>
                <small>Completed</small>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="dashboard-card search-card">
            <input
              type="text"
              placeholder="Search complaints..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {/* Complaints List */}
          <ComplaintList
            complaints={filteredComplaints}
            loading={loading}
            onViewDetails={(complaint) => {
              setSelectedComplaint(complaint);
              setActiveView('details');
            }}
            isAdmin={isAdmin}
          />
        </div>
      </main>
    </div>
  );
}

export default ComplaintManagement;